import React from 'react';
import { Button, Badge } from '../../ui';

const money = (v) => (Number(v) / 100).toFixed(2);

// mode: 'subscribe' | 'current' | 'upgrade' | 'downgrade' | 'renew'
const CTA = {
  subscribe: { label: 'Subscribe', icon: null, variant: 'primary' },
  upgrade: { label: 'Upgrade', icon: 'fa-arrow-up', variant: 'primary' },
  downgrade: { label: 'Switch to this plan', icon: 'fa-arrow-down-up-across-line', variant: 'outline' },
  renew: { label: 'Renew', icon: 'fa-rotate-right', variant: 'primary' },
};

const PlanCard = ({
  plan,
  isPopular = false,
  mode = 'subscribe',
  billingCycle = 'monthly',
  onSelect,
  loading = false,
}) => {
  const isCurrentPlan = mode === 'current';
  const price = billingCycle === 'yearly' ? plan.price_yearly : plan.price_monthly;
  const monthlyEquiv = billingCycle === 'yearly' ? (plan.price_yearly / 12 / 100).toFixed(2) : null;

  const monetization = [];
  if (plan.allow_avod) monetization.push('AVOD');
  if (plan.allow_svod) monetization.push('SVOD');
  if (plan.allow_ads) monetization.push('Video Ads');
  if (plan.allow_preroll) monetization.push('Pre-roll');
  if (plan.allow_midroll) monetization.push('Mid-roll');
  if (plan.allow_postroll) monetization.push('Post-roll');
  if (plan.allow_google_ads) monetization.push('Google Ad Manager');
  if (plan.allow_programmatic_ads) monetization.push('Programmatic');

  const features = [
    ...(plan.features || []),
    `${plan.max_streams} live stream${plan.max_streams > 1 ? 's' : ''}`,
    `${plan.max_storage_gb} GB storage`,
    `${plan.max_bandwidth_gb} GB bandwidth`,
    ...(plan.allow_custom_domains ? ['White-label portal & custom domain'] : []),
    ...(plan.allow_switcher ? ['Multi-camera live switcher'] : []),
    ...(plan.allow_failover ? ['Auto-failover to VOD'] : []),
    ...(plan.allow_iptv ? [`IPTV channels${plan.max_iptv_channels ? ` (${plan.max_iptv_channels})` : ''}`] : []),
    ...(plan.allow_cdn_config ? ['CDN configuration'] : []),
    ...(plan.allow_team ? [`Team management${plan.max_team_members ? ` (${plan.max_team_members} members)` : ''}`] : []),
  ];

  return (
    <div
      className={`tw-relative tw-flex tw-h-full tw-flex-col tw-rounded-2xl tw-border tw-bg-ink-900 tw-p-6 ${
        isPopular ? 'tw-border-brand tw-shadow-glow' : 'tw-border-line'
      }`}
    >
      {isPopular && (
        <span className="tw-absolute tw--top-3 tw-left-1/2 tw--translate-x-1/2 tw-rounded-full tw-bg-brand tw-px-3 tw-py-1 tw-text-xs tw-font-bold tw-text-white">
          Most Popular
        </span>
      )}

      <h3 className="tw-text-lg tw-font-bold tw-text-heading">{plan.name}</h3>
      <p className="tw-mt-1 tw-text-sm tw-text-content-muted">{plan.description}</p>

      <div className="tw-my-5">
        <span className="tw-text-3xl tw-font-extrabold tw-text-heading">${money(price)}</span>
        <span className="tw-text-sm tw-text-content-faint">/{billingCycle === 'yearly' ? 'year' : 'month'}</span>
        {billingCycle === 'yearly' && monthlyEquiv && (
          <div className="tw-mt-1 tw-text-xs tw-text-content-faint">${monthlyEquiv}/mo billed annually</div>
        )}
      </div>

      <ul className="tw-mb-6 tw-flex-1 tw-space-y-2 tw-text-sm">
        {features.map((f, i) => (
          <li key={i} className="tw-flex tw-gap-2 tw-text-content">
            <i className="fa-solid fa-check tw-mt-0.5 tw-text-good" />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      {monetization.length > 0 && (
        <div className="tw-mb-5">
          <div className="tw-mb-2 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wider tw-text-content-faint">
            Monetization
          </div>
          <div className="tw-flex tw-flex-wrap tw-gap-1.5">
            {monetization.map((m) => <Badge key={m} tone="warn">{m}</Badge>)}
          </div>
        </div>
      )}

      {isCurrentPlan ? (
        <div className="tw-space-y-2">
          <Button variant="secondary" className="tw-w-full" disabled>
            <i className="fa-solid fa-check" /> Current Plan
          </Button>
        </div>
      ) : (
        (() => {
          const cta = CTA[mode] || CTA.subscribe;
          return (
            <Button
              variant={isPopular || cta.variant === 'primary' ? 'primary' : 'outline'}
              className="tw-w-full"
              icon={cta.icon || undefined}
              disabled={loading}
              onClick={() => onSelect && onSelect(plan, billingCycle)}
            >
              {loading ? 'Loading…' : mode === 'renew' ? `Renew ${plan.name}` : cta.label}
            </Button>
          );
        })()
      )}
    </div>
  );
};

export default PlanCard;
